import {
  Home,
  Phone,
  FileText,
  FileAudio,
  BarChart3,
  RefreshCw,
  Upload,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { User } from '../types';

export type TabId =
  | 'overview'
  | 'calls'
  | 'documents'
  | 'transcripts'
  | 'analytics'
  | 'sync'
  | 'data-import';

interface NavItem {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { id: 'overview', label: 'Ubersicht', icon: <Home className="w-5 h-5" /> },
  { id: 'calls', label: 'Anrufe', icon: <Phone className="w-5 h-5" /> },
  { id: 'documents', label: 'SEE Dokumente', icon: <FileText className="w-5 h-5" /> },
  { id: 'transcripts', label: 'Transkripte', icon: <FileAudio className="w-5 h-5" /> },
  { id: 'analytics', label: 'Analysen', icon: <BarChart3 className="w-5 h-5" /> },
  { id: 'sync', label: 'Sync Status', icon: <RefreshCw className="w-5 h-5" /> },
  { id: 'data-import', label: 'Datenimport', icon: <Upload className="w-5 h-5" /> },
];

interface SidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  user: User | null;
  onLogout: () => void;
}

export default function Sidebar({
  activeTab,
  onTabChange,
  user,
  onLogout,
}: SidebarProps) {
  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Phone className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="font-bold text-gray-900">Monuite</h1>
            <p className="text-xs text-gray-500">Call Intelligence</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onTabChange(item.id)}
            className={clsx(
              'w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
              activeTab === item.id
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            )}
          >
            <span
              className={clsx(
                activeTab === item.id ? 'text-primary-600' : 'text-gray-400'
              )}
            >
              {item.icon}
            </span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3 mb-3">
          <div className="h-9 w-9 bg-gray-200 rounded-full flex items-center justify-center">
            <UserIcon className="h-5 w-5 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || ''}
            </p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <LogOut className="w-5 h-5 text-gray-400" />
          <span>Abmelden</span>
        </button>
      </div>
    </aside>
  );
}
